import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { QrCode, UserPlus, CreditCard, Gift } from "lucide-react";

const steps = [
  {
    icon: QrCode,
    number: "01",
    title: "Vous installez vos QR de fidélité",
    body:
      "Nous vous envoyons un sticker QR au comptoir et un autocollant aux tables. Pas de routeur à changer, pas de matériel à brancher.",
  },
  {
    icon: UserPlus,
    number: "02",
    title: "Vos clients s'inscrivent en un scan",
    body:
      "Le client scanne, accepte les conditions et partage son numéro. Il débloque aussitôt votre cadeau de bienvenue — Wifi, pâtisserie offerte, remise ou message selon votre commerce — sa carte de fidélité, et l'accès à vos campagnes.",
  },
  {
    icon: CreditCard,
    number: "03",
    title: "Une carte de fidélité s'ouvre",
    body:
      "Sa carte de fidélité numérique apparaît immédiatement sur son téléphone, avec votre logo, vos couleurs, et un compteur à 1/20.",
  },
  {
    icon: Gift,
    number: "04",
    title: "Récompensez à votre rythme",
    body:
      "Vous choisissez le déclencheur : un nombre de visites, ou un montant d'achats validé par les tickets de caisse. Au seuil atteint, le client débloque la récompense que vous avez fixée — produit offert, remise ou geste maison.",
  },
];

const stepVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.12,
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  }),
};

export const HowItWorks = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section
      id="fonctionnement"
      ref={ref}
      className="relative py-20 lg:py-28 bg-gradient-to-b from-transparent via-secondary/30 to-transparent"
    >
      <div className="container mx-auto px-4 lg:px-6 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-primary/8 text-primary border border-primary/15 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            Comment ça marche
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight mb-4">
            Quatre étapes,{" "}
            <span className="text-gradient">prêt en moins d'une journée</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Pas de matériel, pas de formation. Nous fournissons le QR, le portail
            d'inscription et le tableau de bord.
          </p>
        </motion.div>

        <div className="relative">
          <div className="hidden lg:block absolute top-[60px] left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.number}
                  custom={i}
                  variants={stepVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  className="relative text-center"
                >
                  <div className="relative inline-flex items-center justify-center mb-6">
                    <div className="absolute inset-0 rounded-full bg-primary/10 blur-xl" />
                    <div className="relative rounded-full bg-card border-2 border-primary/30 p-5 shadow-lg shadow-primary/10">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <div className="absolute -top-2 -right-2 rounded-full bg-primary text-primary-foreground text-xs font-bold w-7 h-7 flex items-center justify-center shadow-md">
                      <bdi>{step.number}</bdi>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
                    {step.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
